import { Link } from "react-router-dom";
import { ArrowLeft, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useCart } from "@/lib/cart";

export default function CartReview() {
  const { items, setQty, subtotal } = useCart();
  const allDigital = items.length > 0 && items.every((i) => i.digital);
  const shipping = items.length === 0 || allDigital || subtotal >= 300 ? 0 : 10;
  const total = subtotal + shipping;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#1A1A1A]" data-testid="cart-review-page">
      <header className="flex items-center gap-4 px-6 md:px-12 py-6 border-b border-[#C5A059]/20 bg-white">
        <Link to="/shop" data-testid="back-to-shop-link" aria-label="Back to shop"
          className="text-[#4A4A4A] hover:text-[#9E7B3A] transition-colors duration-300">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <h1 className="font-serif text-2xl font-light">Your Cart</h1>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-14 grid grid-cols-1 lg:grid-cols-5 gap-14">
        <div className="lg:col-span-3" data-testid="cart-review-items">
          {items.length === 0 && (
            <p className="text-sm text-[#4A4A4A] bg-white border border-[#C5A059]/25 p-8" data-testid="cart-empty">
              Your cart is empty. <Link to="/shop" className="text-[#9E7B3A] underline">Browse the collection</Link>.
            </p>
          )}
          {items.map((i) => (
            <div key={i.key || i.id} className="flex gap-5 items-center border-b border-[#C5A059]/15 pb-6 mb-6" data-testid={`cart-item-${i.key || i.id}`}>
              <Link to={`/shop/product/${i.id}`}>
                <img src={i.image_url || "/assets/logo.png"} alt={i.name} className="w-20 h-24 object-cover bg-white border border-[#C5A059]/15" />
              </Link>
              <div className="flex-1">
                <Link to={`/shop/product/${i.id}`} className="hover:text-[#9E7B3A] transition-colors duration-300">
                  <p className="font-serif text-lg font-light">{i.name}</p>
                </Link>
                <p className="text-xs text-[#4A4A4A]">{i.size} · ${i.price.toFixed(2)}</p>
                <div className="flex items-center gap-3 mt-3">
                  <button onClick={() => setQty(i.key || i.id, i.qty - 1)} data-testid={`qty-minus-${i.key || i.id}`} aria-label="Decrease"
                    className="w-7 h-7 border border-[#C5A059]/40 flex items-center justify-center hover:border-[#9E7B3A] transition-colors duration-300">
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="text-sm w-6 text-center">{i.qty}</span>
                  <button onClick={() => setQty(i.key || i.id, i.qty + 1)} data-testid={`qty-plus-${i.key || i.id}`} aria-label="Increase"
                    className="w-7 h-7 border border-[#C5A059]/40 flex items-center justify-center hover:border-[#9E7B3A] transition-colors duration-300">
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm mb-3">${(i.price * i.qty).toFixed(2)}</p>
                <button onClick={() => setQty(i.key || i.id, 0)} data-testid={`remove-item-${i.key || i.id}`} aria-label="Remove"
                  className="text-[#4A4A4A] hover:text-red-700 transition-colors duration-300"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="lg:col-span-2">
            <div className="bg-white border border-[#C5A059]/25 p-8 sticky top-8" data-testid="cart-summary">
              <p className="text-xs tracking-[0.35em] uppercase text-[#9E7B3A] mb-6">Summary</p>
              <div className="space-y-3 text-sm mb-6">
                <div className="flex justify-between"><span className="text-[#4A4A4A]">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between">
                  <span className="text-[#4A4A4A]">Express Shipping</span>
                  <span>{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between font-medium border-t border-[#C5A059]/20 pt-3">
                  <span>Total</span><span>${total.toFixed(2)} AUD</span>
                </div>
              </div>
              {shipping > 0 && (
                <p className="text-xs text-[#9E7B3A] mb-6">Free express shipping on orders over $300.</p>
              )}
              <Link to="/shop/checkout" data-testid="proceed-to-checkout"
                className="group w-full flex items-center justify-center gap-2 bg-[#1A1A1A] text-white text-xs tracking-[0.25em] uppercase py-4 hover:bg-[#9E7B3A] transition-colors duration-300">
                Proceed to Checkout
                <ArrowRight className="w-4 h-4 text-[#C5A059] transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/shop" data-testid="continue-shopping-link"
                className="block text-center text-xs tracking-[0.2em] uppercase text-[#4A4A4A] hover:text-[#9E7B3A] mt-5 transition-colors duration-300">
                Continue Shopping
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
